import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Card,
} from '@mui/material';
import {
  AutoFixHigh,
  ArrowBack,
  InsertDriveFile,
  Schedule,
} from '@mui/icons-material';
import { SideDrawer } from '../../components/common/SideDrawer';
import type { Issue } from '../../store/api/githubApi';
import { formatEffortMinutes, parseEffortMinutes } from './simulatorUtils';

interface RemediationPreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedIssues: Issue[];
  onRemediate: () => void;
  isFixing?: boolean;
}

export const RemediationPreviewDrawer: React.FC<RemediationPreviewDrawerProps> = ({
  open,
  onClose,
  selectedIssues,
  onRemediate,
  isFixing = false,
}) => {
  const count = selectedIssues.length;
  const totalMinutes = selectedIssues.reduce((sum, i) => sum + parseEffortMinutes(i), 0);
  const effortFormatted = formatEffortMinutes(totalMinutes);
  const uniqueFiles = new Set(selectedIssues.map((i) => i.file_path).filter(Boolean));

  const getSeverityStyle = (sev?: string) => {
    switch ((sev ?? '').toLowerCase()) {
      case 'critical':
        return { label: 'Critical', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
      case 'high':
        return { label: 'High', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
      case 'medium':
        return { label: 'Medium', color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
      case 'low':
        return { label: 'Low', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' };
      default:
        return { label: 'Info', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
    }
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <AutoFixHigh sx={{ fontSize: 22, color: '#2563eb' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Remediation Preview
          </Typography>
        </Box>
      }
      description="Review the selected findings carried over from the Remediation Planner before triggering automated fix generation."
      width={{ xs: '100%', sm: 540 }}
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<ArrowBack />}
            disabled={isFixing}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              color: '#475569',
              borderColor: '#cbd5e1',
              px: 2.5,
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f8fafc',
              },
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={onRemediate}
            disabled={count === 0 || isFixing}
            startIcon={<AutoFixHigh />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#0f172a',
              color: '#ffffff',
              px: 3,
              py: 1,
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
              '&:hover': {
                bgcolor: '#1e293b',
              },
              '&.Mui-disabled': {
                bgcolor: '#e2e8f0',
                color: '#94a3b8',
              },
            }}
          >
            Remediate ({count} fix{count !== 1 ? 'es' : ''})
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Top Summary Banner */}
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${count} selected fix${count !== 1 ? 'es' : ''}`}
              size="small"
              sx={{
                fontWeight: 800,
                fontSize: '0.78rem',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                border: '1px solid #bfdbfe',
              }}
            />
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
              ready for PR creation
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InsertDriveFile sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                {uniqueFiles.size} file{uniqueFiles.size !== 1 ? 's' : ''}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Schedule sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                {effortFormatted}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Selected Issues List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#64748b',
              fontSize: '0.72rem',
            }}
          >
            Queued Findings ({count})
          </Typography>

          {selectedIssues.map((issue, idx) => {
            const sev = getSeverityStyle(issue.severity);
            const isAiFixable = ['trivial', 'simple', 'moderate'].includes(
              issue.ai_fix_complexity ?? ''
            ) || (issue.category === 'dependencies' && Boolean(issue.safe_version));

            return (
              <Card
                key={issue.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {/* Title & Badges */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      lineHeight: 1.35,
                    }}
                  >
                    {idx + 1}. {issue.title}
                  </Typography>

                  <Chip
                    label={sev.label}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: sev.bg,
                      color: sev.color,
                      border: `1px solid ${sev.border}`,
                      flexShrink: 0,
                    }}
                  />
                </Box>

                {/* File path & Category */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: '#475569',
                      textTransform: 'capitalize',
                      fontSize: '0.75rem',
                    }}
                  >
                    {issue.category}
                  </Typography>

                  {issue.file_path && (
                    <>
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                        ·
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <InsertDriveFile sx={{ fontSize: 13, color: '#94a3b8' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: '#64748b',
                            fontSize: '0.75rem',
                          }}
                        >
                          {issue.file_path.split('/').pop()}
                          {issue.line_start ? `:${issue.line_start}` : ''}
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                    ·
                  </Typography>

                  <Chip
                    label={isAiFixable ? 'AI Fix Ready' : 'Guided Fix'}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      bgcolor: isAiFixable ? '#f0fdf4' : '#f8fafc',
                      color: isAiFixable ? '#16a34a' : '#64748b',
                      border: `1px solid ${isAiFixable ? '#bbf7d0' : '#e2e8f0'}`,
                    }}
                  />
                </Box>

                {/* Short explanation if present */}
                {issue.ai_explanation && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '0.8rem',
                      lineHeight: 1.4,
                      mt: 0.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {issue.ai_explanation}
                  </Typography>
                )}
              </Card>
            );
          })}
        </Box>
      </Box>
    </SideDrawer>
  );
};

export default RemediationPreviewDrawer;
